/**
 * 指定フォーマット出力（#3070）のフォーマット管理 API。
 *
 * フォーマット本体（.xlsx）は Storage に置き、メタ情報を Firestore の
 * export_templates コレクションに持つ。
 *
 * 仕様書: documents/develop/issues/3070-excel-export/excel-export-feature.md §5
 */
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, deleteObject, getBytes } from 'firebase/storage';
import { db, storage } from '../../config';
import { type ExportTemplate, type ExportTemplateInput } from '../../../../types/exportTemplate';
import { EXPORT_TEMPLATE_MAX_COUNT_PER_PATTERN } from '../../../../constants/enum';
import { ValidationError } from '../../../../modules/error';

const COLLECTION = 'export_templates';

/** フォーマット本体の Storage パスを決める（登録した企業ごとに分ける） */
const buildStoragePath = (companyId: string, fileName: string): string =>
  `export_templates/${companyId}/${Date.now()}_${fileName}`;

/**
 * パターンに紐づくフォーマット一覧を取得する。
 * フォーマットは登録した企業の中でのみ見える（§4）。
 */
export const getExportTemplates = async (
  patternId: string,
  companyId: string,
): Promise<ExportTemplate[]> => {
  // company_id はクエリ条件として必須。Firestore ルールの list が同じ条件を要求するため、
  // これを外すとクエリ全体が permission-denied になる（#3207）。
  // created_at の並べ替えを同時に行うと複合インデックスが増えるが、
  // 1パターンあたりの件数は上限20件と少ないため、並べ替えはクライアント側で行う。
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('pattern_id', '==', patternId),
      where('company_id', '==', companyId),
    ),
  );
  return snap.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<ExportTemplate, 'id'>) }))
    .sort((a, b) => (b.created_at?.toMillis?.() ?? 0) - (a.created_at?.toMillis?.() ?? 0));
};

/** フォーマットを1件取得する */
export const getExportTemplate = async (templateId: string): Promise<ExportTemplate | null> => {
  const snap = await getDoc(doc(db, COLLECTION, templateId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<ExportTemplate, 'id'>) };
};

/** フォーマット本体（.xlsx）を Storage から取得する */
export const getExportTemplateFile = async (filePath: string): Promise<Uint8Array> => {
  const buffer = await getBytes(ref(storage, filePath));
  return new Uint8Array(buffer);
};

/**
 * フォーマットを登録する。
 * ファイルを Storage に上げてから Firestore にメタ情報を書く。
 */
export const postExportTemplate = async (
  file: File,
  input: Omit<ExportTemplateInput, 'file_path' | 'file_name'>,
): Promise<string> => {
  // 1パターンあたりの上限を超えないようにする。
  // company_id はルールの list 条件を満たすために必須（#3207）
  const existing = await getDocs(
    query(
      collection(db, COLLECTION),
      where('pattern_id', '==', input.pattern_id),
      where('company_id', '==', input.company_id),
    ),
  );
  if (existing.size >= EXPORT_TEMPLATE_MAX_COUNT_PER_PATTERN) {
    throw new ValidationError(
      `1つの読取パターンに登録できるフォーマットは${EXPORT_TEMPLATE_MAX_COUNT_PER_PATTERN}個までです。`,
    );
  }

  const storagePath = buildStoragePath(input.company_id, file.name);
  await uploadBytes(ref(storage, storagePath), file);

  const docRef = await addDoc(collection(db, COLLECTION), {
    ...input,
    file_path: storagePath,
    file_name: file.name,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return docRef.id;
};

/** フォーマットの設定（名称・対象シート）を更新する。ファイル自体は差し替えない */
export const updateExportTemplate = async (
  templateId: string,
  input: Partial<Pick<ExportTemplate, 'template_name' | 'target_sheets' | 'used_field_codes'>>,
): Promise<void> => {
  await updateDoc(doc(db, COLLECTION, templateId), {
    ...input,
    updated_at: serverTimestamp(),
  });
};

/**
 * フォーマットのファイルを差し替える（#3070）。
 * ドキュメントIDは変えずに、Storage のファイルとメタ情報だけを入れ替える。
 * 既定フォーマットの設定など、将来 ID を参照する機能が壊れないようにするため。
 */
export const replaceExportTemplateFile = async (
  template: ExportTemplate,
  file: File,
  input: Pick<
    ExportTemplate,
    'template_name' | 'target_sheets' | 'all_sheets' | 'used_field_codes'
  >,
): Promise<void> => {
  if (!template.id) return;

  // 新しいファイルを先に上げてから、メタ情報を更新する。
  // 先に古いファイルを消すと、アップロードに失敗したときに参照先を失うため。
  const storagePath = buildStoragePath(template.company_id, file.name);
  await uploadBytes(ref(storage, storagePath), file);

  await updateDoc(doc(db, COLLECTION, template.id), {
    ...input,
    file_path: storagePath,
    file_name: file.name,
    updated_at: serverTimestamp(),
  });

  // 差し替え前のファイルを消す（失敗しても差し替え自体は成立している）
  try {
    await deleteObject(ref(storage, template.file_path));
  } catch {
    // 既に無い場合などは無視する
  }
};

/** フォーマットを削除する（Storage のファイルも消す） */
export const deleteExportTemplate = async (template: ExportTemplate): Promise<void> => {
  if (!template.id) return;
  // Storage の削除が失敗しても Firestore 側は消す（孤児ファイルより、
  // 一覧に残り続けて選べてしまうほうが利用者に実害があるため）
  try {
    await deleteObject(ref(storage, template.file_path));
  } catch {
    // 既に無い場合などは無視する
  }
  await deleteDoc(doc(db, COLLECTION, template.id));
};
